
import React, { useState } from 'react';
import { UserProfile, Transaction } from '../../../app/domain/types/index.ts';
import { CreditCard, ShieldCheck, Clock, ChevronRight, Download, Plus, Receipt } from 'lucide-react';

interface PaymentPortalProps {
  user: UserProfile;
}

const PaymentPortal: React.FC<PaymentPortalProps> = ({ user }) => {
  // Mock Data
  const transactions: Transaction[] = [
    { id: 'TXN-1234', date: 'Dec 01, 2025', description: 'Consultation: Dr. Smith', amount: 150.00, status: 'completed', method: 'Visa •••• 4242' },
    { id: 'TXN-1235', date: 'Nov 15, 2025', description: 'Premium Subscription (Monthly)', amount: 29.99, status: 'completed', method: 'Visa •••• 4242' },
    { id: 'TXN-1236', date: 'Oct 15, 2025', description: 'Premium Subscription (Monthly)', amount: 29.99, status: 'completed', method: 'Visa •••• 4242' },
  ];

  const cards = [
    { id: '1', brand: 'Visa', last4: '4242', expiry: '12/26', isDefault: true },
    { id: '2', brand: 'Mastercard', last4: '8888', expiry: '08/25', isDefault: false },
  ];

  return (
    <div className="h-full flex flex-col bg-[#FAFAFA] dark:bg-[#0f0f0f] relative">

      {/* Header */}
      <div className="p-6 md:p-8 pb-4 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md sticky top-0 z-20">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payments & Billing</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage your payment methods, subscriptions, and billing history.</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6 md:p-8 no-scrollbar">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8">

          {/* Left Column: Payment Methods & Subscriptions */}
          <div className="md:col-span-7 space-y-8">

            {/* Subscription Card */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 dark:from-black dark:to-stone-900 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-l from-white/5 to-transparent -mr-10 -mt-10 rounded-full"></div>
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <p className="text-gray-400 text-xs uppercase tracking-wider font-bold mb-1">Current Plan</p>
                    <h2 className="text-3xl font-bold">Premium Plan</h2>
                  </div>
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/50 rounded-full text-xs font-bold">Active</span>
                </div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-4xl font-bold">$29.99</span>
                  <span className="text-gray-400">/ month</span>
                </div>
                <div className="space-y-2 mb-8">
                  <p className="text-sm text-gray-300 flex items-center gap-2"><ShieldCheck size={16} className="text-green-400" /> Next billing date: Dec 15, 2025</p>
                  <p className="text-sm text-gray-300 flex items-center gap-2"><CreditCard size={16} /> Visa ending in 4242</p>
                </div>
                <div className="flex gap-3">
                  <button className="px-6 py-2.5 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-colors">Manage Subscription</button>
                  <button className="px-6 py-2.5 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-colors">Change Plan</button>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Payment Methods</h3>
                <button className="text-sm font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline">
                  <Plus size={16} /> Add New
                </button>
              </div>
              <div className="space-y-3">
                {cards.map(card => (
                  <div key={card.id} className="bg-white dark:bg-stone-800 p-4 rounded-2xl border border-stone-200 dark:border-stone-700 flex items-center justify-between group hover:border-blue-200 dark:hover:border-blue-800 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-8 bg-stone-100 dark:bg-stone-700 rounded flex items-center justify-center font-bold text-xs text-gray-500 dark:text-gray-400">
                        {card.brand}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                          •••• {card.last4}
                          {card.isDefault && <span className="px-2 py-0.5 bg-stone-100 dark:bg-stone-700 text-xs text-gray-500 rounded text-[10px] uppercase">Default</span>}
                        </p>
                        <p className="text-xs text-gray-500">Expires {card.expiry}</p>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600 dark:hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">Edit</button>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column: Transaction History */}
          <div className="md:col-span-5 bg-white dark:bg-stone-800 rounded-3xl border border-stone-200 dark:border-stone-700 p-6 h-fit">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Transaction History</h3>
              <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white">
                <Download size={18} />
              </button>
            </div>

            <div className="space-y-6 relative">
              <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-stone-100 dark:bg-stone-700"></div>
              {transactions.map((txn) => (
                <div key={txn.id} className="relative pl-10">
                  <div className="absolute left-[13px] top-1 w-2.5 h-2.5 rounded-full bg-stone-300 dark:bg-stone-600 ring-4 ring-white dark:ring-stone-800"></div>
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-bold text-gray-900 dark:text-white text-sm">{txn.description}</p>
                    <span className="font-bold text-gray-900 dark:text-white text-sm">${txn.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                    <span>{txn.date}</span>
                    <button className="flex items-center gap-1 hover:text-blue-500">
                      <Receipt size={12} /> Receipt
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button className="w-full mt-8 py-3 bg-stone-50 dark:bg-stone-700/50 text-gray-600 dark:text-gray-300 rounded-xl text-sm font-bold hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors">
              View All Transactions
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PaymentPortal;
